import {
  ApiAction,
  ApiContext,
  ApiItemType,
  ApiReturnFrontendPermission,
} from '@highimpact-frontend/api-types';
import { useIsMobile } from '@highimpact/react-hooks';
import { Typography } from '@mui/material';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Component props.
 */
type PermissionDecoratorV2Props = {
  item: string;
  project_id?: number;
  site_id?: number;
  company_id?: number;
  extra?: Record<string, any>;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  onResolve?: (item: string, action: ApiAction) => void;
};

const DEFAULT_ACTION: ApiAction = 'hide';

/**
 * Fetches permissions for a given item and context.
 *
 * Returns an object containing the action to take (hide, disable, hide_with_message, display)
 */
async function fetcher(url: string, params: ApiContext) {
  const token = sessionStorage.getItem('security_token');
  if (!token) throw new Error('Security token not found in sessionStorage');

  const res = await fetch(`/api/permissions${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error('Failed to fetch permissions');
  return res.json() as Promise<ApiReturnFrontendPermission>;
}

/**
 * A React component that decorates its children based on permissions fetched from the backend.
 * It can hide, disable, or show a message instead of the children based on the permissions.
 * The component takes an item name and context parameters (project_id, site_id, company_id, extra_params) to determine the permissions.
 *
 * The onResolve callback is called with the item and the resolved action once the permissions are fetched, allowing parent components to react to the permission state if needed.
 *
 */
export const PermissionDecorator = forwardRef<
  HTMLDivElement,
  PermissionDecoratorV2Props
>(
  (
    {
      children,
      item,
      project_id,
      site_id,
      company_id,
      extra,
      style,
      className,
      onResolve,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const extraSnapshot = JSON.stringify(extra ?? {});
    const params: ApiContext = useMemo(
      () => ({
        project_id: project_id ? Number(project_id) : null,
        site_id: site_id ? Number(site_id) : null,
        company_id: company_id ? Number(company_id) : null,
        item: item as ApiItemType,
        extra: { ...(extra ?? {}), mobile: isMobile },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [company_id, item, project_id, site_id, extraSnapshot, isMobile]
    );
    const paramsKey = useMemo(() => JSON.stringify(params), [params]);
    const onResolveRef = useRef(onResolve);
    onResolveRef.current = onResolve;

    /**
     * Data state handling.
     */
    const [data, setData] = useState<ApiReturnFrontendPermission | null>(null);

    useEffect(() => {
      let isMounted = true;
      const requestParams = JSON.parse(paramsKey) as ApiContext;

      fetcher('/frontend-permissions', requestParams)
        .then((res) => {
          if (isMounted) {
            setData(res);
            const action = res.action ?? DEFAULT_ACTION;
            onResolveRef.current?.(requestParams.item, action);
          }
        })
        .catch((err) => {
          if (isMounted) {
            onResolveRef.current?.(requestParams.item, DEFAULT_ACTION);
          }
        });

      return () => {
        isMounted = false;
      };
    }, [paramsKey]);
    // -----------------------------

    // Keep showing the last resolved permission while refetching so toolbar
    // buttons do not flash off/on when unrelated UI state triggers a re-render.
    if (!data) {
      return null;
    }

    const action: ApiAction = data['action'] ?? DEFAULT_ACTION;

    if (action === 'hide') {
      return null;
    }

    if (action === 'hide_with_message') {
      return (
        <Typography>
          Vous n'avez pas les permissions nécessaires pour accéder à cette page
        </Typography>
      );
    }

    if (action === 'disable') {
      return (
        <div
          ref={ref}
          style={{ opacity: 0.5, pointerEvents: 'none' }}
          className={className}
        >
          {children}
        </div>
      );
    }

    return (
      <div ref={ref} {...props} style={style} className={className}>
        {children}
      </div>
    );
  }
);
